import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  DOC_EXTENSIONS,
  IMAGE_EXTENSIONS,
  MAX_CHAT_ATTACHMENTS,
  getFileType,
  isImageExtension,
  isDocExtension,
  isAudioExtension,
  validateUploadSize,
} from '@/lib/fileConstants';

export interface ChatFileAttachment {
  id: string;
  file: File;
  fileName: string;
  fileType: string;
  status: 'uploading' | 'parsing' | 'ready' | 'error';
  contentText?: string;
  tokenCount?: number;
  error?: string;
  storagePath?: string;
  dbId?: string;
}

export function useChatFiles(allowImages = false, allowAudio = false) {
  const { user } = useAuth();
  const [attachments, setAttachments] = useState<ChatFileAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFile = useCallback(async (file: File) => {
    if (!user) return;

    const isImage = isImageExtension(file.name);
    const isDoc = isDocExtension(file.name);
    const isAudio = isAudioExtension(file.name);

    if (!isDoc && !(isImage && allowImages) && !(isAudio && allowAudio)) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (isAudio && !allowAudio) {
        toast.error(`O modelo selecionado não aceita áudio. Troque para um modelo com suporte.`);
      } else if (isImage && !allowImages) {
        toast.error(`O modelo selecionado não aceita imagens. Troque para um modelo com visão.`);
      } else {
        toast.error(`Tipo não suportado: .${ext}`);
      }
      return;
    }

    const sizeError = validateUploadSize(file);
    if (sizeError) {
      toast.error(sizeError);
      return;
    }

    setAttachments(prev => {
      if (prev.length >= MAX_CHAT_ATTACHMENTS) {
        toast.error(`Máximo de ${MAX_CHAT_ATTACHMENTS} anexos por mensagem`);
        return prev;
      }
      const id = crypto.randomUUID();
      const isImgFile = isImageExtension(file.name);
      const isAudioFile = isAudioExtension(file.name);
      const skipParse = isImgFile || isAudioFile;
      const attachment: ChatFileAttachment = {
        id,
        file,
        fileName: file.name,
        fileType: isImgFile ? 'image' : (isAudioFile ? 'audio' : getFileType(file.name)),
        status: 'uploading',
      };
      // Imagens e áudios vão como base64 nativo, mas ainda precisam
      // chegar no Storage pra sandbox/edge function poderem referenciar.
      processFile(id, file, user.id, skipParse);
      return [...prev, attachment];
    });
  }, [user, allowImages, allowAudio]);

  const processFile = async (attachmentId: string, file: File, userId: string, skipParse = false) => {
    const updateAttachment = (updates: Partial<ChatFileAttachment>) => {
      setAttachments(prev => prev.map(a => a.id === attachmentId ? { ...a, ...updates } : a));
    };

    try {
      // Sanitize file name for storage (remove accents, spaces, special chars)
      const sanitizedName = file.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove accents
        .replace(/[^a-zA-Z0-9._-]/g, '_'); // replace special chars with underscore
      const storagePath = `${userId}/${attachmentId}/${sanitizedName}`;
      const { error: uploadError } = await supabase.storage
        .from('chat-files')
        .upload(storagePath, file);
      if (uploadError) throw uploadError;

      if (skipParse) {
        updateAttachment({ status: 'ready', storagePath });
        return;
      }

      updateAttachment({ status: 'parsing', storagePath });

      // Call parse-document edge function
      const { data, error } = await supabase.functions.invoke('parse-document', {
        body: {
          chatFileId: attachmentId,
          storageBucket: 'chat-files',
          storagePath,
          fileName: file.name,
          fileType: getFileType(file.name),
        },
      });

      if (error) throw error;

      updateAttachment({
        status: 'ready',
        contentText: data?.content_text || '',
        tokenCount: data?.token_count || 0,
      });
    } catch (err: any) {
      // Tenta extrair mensagem real do edge function (FunctionsHttpError
      // do supabase-js encapsula a Response em err.context).
      let detail = err?.message || '';
      try {
        const body = await err?.context?.json?.();
        if (body?.error) detail = body.error;
      } catch { /* ignora se nao for JSON */ }
      updateAttachment({ status: 'error', error: detail });
      toast.error(detail ? `Erro ao processar "${file.name}": ${detail}` : `Erro ao processar "${file.name}"`);
    }
  };

  const removeFile = useCallback((attachmentId: string) => {
    setAttachments(prev => {
      const att = prev.find(a => a.id === attachmentId);
      // Clean up storage if uploaded
      if (att?.storagePath) {
        supabase.storage.from('chat-files').remove([att.storagePath]);
      }
      return prev.filter(a => a.id !== attachmentId);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const allReady = attachments.length > 0 && attachments.every(a => a.status === 'ready');
  const hasAttachments = attachments.length > 0;
  const isProcessing = attachments.some(a => a.status === 'uploading' || a.status === 'parsing');

  return {
    attachments,
    addFile,
    removeFile,
    clearAttachments,
    openFilePicker,
    fileInputRef,
    allReady,
    hasAttachments,
    isProcessing,
  };
}
